/* eslint-disable no-console */
import {message} from 'antd';
import axios, {AxiosRequestConfig} from 'axios';

import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import {APIType, APITypeEnum} from '../../interface/tableAPIType';
import {ensureAIToken} from '../ClimateDB/aiTokenAPI';

/** The ESG tab always uploads under this product code. */
export const ESG_PRODUCT = 'esg';

export type AIDocumentStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface AIDocument {
  id: string;
  file_name: string;
  document_type: string;
  document_type_label?: string;
  year: string;
  brsr_section?: string | null;
  brsr_principle?: string | null;
  status: AIDocumentStatus;
  status_label?: string;
  error?: string | null;
  pages?: number | null;
  passages?: number | null;
  uploaded_at: string;
  uploaded_by?: string | null;
}

export interface AIDocumentTypeOption {
  code: string;
  label: string;
  product: string;
  framework: string;
  brsr_section?: string | null;
  brsr_principle?: string | null;
}

export interface AIDocumentOptions {
  products: any[];
  frameworks: any[];
  document_types: AIDocumentTypeOption[];
  extensions: string[];
  max_mb: number;
  direct_max_mb: number;
  statuses?: Record<string, string>;
  display?: {refresh_seconds?: number; years_shown?: number};
  messages?: Record<string, string>;
}

/** Response shape the shared upload drawer expects from its `API` prop. */
interface DrawerResponse {
  status: 'success' | 'error';
  message: string;
  data?: any;
}

const BASE = `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.aiDocuments}`;

const companyId = (): string =>
  useCompanyStore.getState().companyId?.trim() ?? '';

const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const token = await ensureAIToken();

  if (!token) {
    throw new Error('AI token unavailable');
  }

  const response = await axios.request<T>({
    ...config,
    headers: {...config.headers, Authorization: `Bearer ${token}`}
  });

  return response.data;
};

/** The configured message the API sent back, if any. */
export const apiErrorMessage = (err: any, fallback: string): string => {
  const body = err?.response?.data;
  const detail = body?.detail ?? body?.message ?? body?.error;

  if (typeof detail === 'string' && detail) {
    return detail;
  }

  if (typeof detail?.message === 'string') {
    return detail.message;
  }

  return fallback;
};

let optionsRequest: Promise<AIDocumentOptions> | null = null;

/**
 * `GET /ai-documents/options`: dropdowns, limits and messages. Fetched once per
 * page load; a failed request is not cached so the next caller retries.
 */
export const getAIDocumentOptions = async (): Promise<AIDocumentOptions> => {
  optionsRequest =
    optionsRequest ??
    request<AIDocumentOptions>({
      method: CRUDMethods.GET,
      url: `${BASE}/options`
    }).catch((err) => {
      optionsRequest = null;
      throw err;
    });

  return await optionsRequest;
};

export const listAIDocuments = async (filters: {
  year?: string;
  framework?: string;
  documentType?: string;
}): Promise<AIDocument[]> => {
  const data = await request<{documents?: AIDocument[]} | AIDocument[]>({
    method: CRUDMethods.GET,
    url: BASE,
    params: {
      mongo_company_id: companyId(),
      product: ESG_PRODUCT,
      year: filters.year ? filters.year : undefined,
      framework: filters.framework ? filters.framework : undefined,
      document_type:
        filters.documentType && filters.documentType !== 'all'
          ? filters.documentType
          : undefined
    }
  });

  return Array.isArray(data) ? data : data?.documents ?? [];
};

export const deleteAIDocument = async (id: string): Promise<void> => {
  await request({
    method: CRUDMethods.DELETE,
    url: `${BASE}/${id}`,
    params: {mongo_company_id: companyId()}
  });
};

/** A short-lived link to the stored file. */
export const getAIDocumentFileUrl = async (id: string): Promise<string> => {
  const data = await request<{url: string}>({
    method: CRUDMethods.GET,
    url: `${BASE}/${id}/file`,
    params: {mongo_company_id: companyId()}
  });

  return data.url;
};

/** Only valid for a document whose status is `failed`; anything else is a 409. */
export const retryAIDocument = async (id: string): Promise<void> => {
  await request({
    method: CRUDMethods.POST,
    url: `${BASE}/${id}/retry`,
    params: {mongo_company_id: companyId()}
  });
};

const MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png'
};

/**
 * The picker hands over the `File` itself; an older form value may still be
 * bare base64 (`DocumentUploadPreview`'s default).
 */
const toBlob = (location: File | string, fileName: string): Blob => {
  if (location instanceof Blob) {
    return location;
  }

  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const bytes = Uint8Array.from(atob(location), (c) => c.charCodeAt(0));

  return new Blob([bytes], {type: MIME_TYPES[ext] ?? ''});
};

interface UploadDetails {
  mongo_company_id: string;
  year: string;
  product: string;
  framework: string;
  document_type: string;
  file_name: string;
}

interface UploadLink {
  upload_id: string;
  url: string;
  method?: string;
  headers?: Record<string, string>;
  expires_in_s?: number;
}

const UPLOAD_PROGRESS_KEY = 'ai-document-upload';

const showUploadProgress = (percent: number): void => {
  void message.loading({
    content: `Uploading... ${percent}%`,
    key: UPLOAD_PROGRESS_KEY,
    duration: 0
  });
};

const hideUploadProgress = (): void => {
  message.destroy(UPLOAD_PROGRESS_KEY);
};

/** Files up to `direct_max_mb` go in one multipart request. */
const uploadDirect = async (
  details: UploadDetails,
  file: Blob,
  platformUserId?: string
): Promise<AIDocument> => {
  const body = new FormData();
  body.append('file', file, details.file_name);
  body.append('mongo_company_id', details.mongo_company_id);
  body.append('year', details.year);
  body.append('product', details.product);
  body.append('framework', details.framework);
  body.append('document_type', details.document_type);
  if (platformUserId) {
    body.append('platform_user_id', platformUserId);
  }

  const data = await request<{document: AIDocument}>({
    method: CRUDMethods.POST,
    url: BASE,
    data: body
  });

  return data.document;
};

const getUploadLink = async (
  details: UploadDetails,
  size: number
): Promise<UploadLink> =>
  await request<UploadLink>({
    method: CRUDMethods.POST,
    url: `${BASE}/upload-link`,
    data: {...details, size}
  });

/**
 * Sends the bytes straight to the upload link. Only the headers the link was
 * signed with are sent: no Authorization, and no Content-Type unless the link
 * asks for one, since S3 refuses a request whose headers differ from the
 * signature.
 */
const putToUploadLink = async (link: UploadLink, file: Blob): Promise<void> => {
  const headers = link.headers ?? {};
  const signsContentType = Object.keys(headers).some(
    (name) => name.toLowerCase() === 'content-type'
  );

  await axios.request({
    method: link.method ?? CRUDMethods.PUT,
    url: link.url,
    headers,
    data: signsContentType ? file : file.slice(0, file.size, ''),
    onUploadProgress: (event) => {
      const total = event.total ?? file.size;
      showUploadProgress(total ? Math.round((event.loaded * 100) / total) : 0);
    }
  });
};

/**
 * Files over `direct_max_mb` (up to `max_mb`): get an upload link, PUT the
 * bytes to it, then register the upload. An expired or refused link (403) is
 * replaced once.
 */
const uploadThroughLink = async (
  details: UploadDetails,
  file: Blob,
  platformUserId?: string
): Promise<AIDocument> => {
  let link = await getUploadLink(details, file.size);

  showUploadProgress(0);
  try {
    try {
      await putToUploadLink(link, file);
    } catch (err: any) {
      if (err?.response?.status !== 403) {
        throw err;
      }
      link = await getUploadLink(details, file.size);
      await putToUploadLink(link, file);
    }
  } catch (err) {
    throw new Error('Upload failed, please try again', {cause: err});
  } finally {
    hideUploadProgress();
  }

  const data = await request<{document: AIDocument}>({
    method: CRUDMethods.POST,
    url: `${BASE}/from-upload`,
    data: {
      ...details,
      upload_id: link.upload_id,
      platform_user_id: platformUserId
    }
  });

  return data.document;
};

/**
 * Adapter for `DocumentUploadDrawerForm`'s `API` prop.
 * POST_NEW uploads a file: in one request up to `direct_max_mb`, through an
 * upload link above that. PUT edits the type, year or name through
 * `PATCH /ai-documents/{id}`.
 */
export const aiDocumentRecord = async (
  type: APIType,
  value: any,
  id?: string
): Promise<DrawerResponse> => {
  try {
    if (type === APITypeEnum.PUT && id) {
      await request({
        method: 'PATCH',
        url: `${BASE}/${id}`,
        data: {
          mongo_company_id: companyId(),
          document_type: value.doc_type,
          year: value.year,
          file_name: value.file_name
        }
      });

      return {status: 'success', message: 'Document updated'};
    }

    const options = await getAIDocumentOptions();
    const file = toBlob(value.location, value.file_name);
    const sizeMB = file.size / 1024 / 1024;

    if (sizeMB > options.max_mb) {
      return {
        status: 'error',
        message: `File must be smaller than ${options.max_mb}MB`
      };
    }

    const details: UploadDetails = {
      mongo_company_id: companyId(),
      year: value.year,
      product: ESG_PRODUCT,
      framework: value.bill_type,
      document_type: value.doc_type,
      file_name: value.file_name
    };
    const platformUserId = value.created_by ?? undefined;

    const document =
      sizeMB <= options.direct_max_mb
        ? await uploadDirect(details, file, platformUserId)
        : await uploadThroughLink(details, file, platformUserId);

    return {
      status: 'success',
      message: 'Document uploaded. It will be ready once it has been read.',
      data: document
    };
  } catch (err: any) {
    console.log('ai documents err:', err);

    return {
      status: 'error',
      message: err?.response
        ? apiErrorMessage(err, 'Oops! Something went wrong')
        : err?.message ?? 'Oops! Something went wrong'
    };
  }
};
